import React from "react";

/**
 * Session 57 · Métier Member Badge.
 *
 * Displays the permanent classification for every user across the platform:
 *   - founding_member → Gold "FOUNDING MEMBER"
 *   - early_adopter   → retired (Session 60.8, renamed Session 60.9); renders null
 *   - admin           → Navy "MÉTIER TEAM"
 *
 * `member_type` is served by the backend on every /auth/me response and
 * every serialized public user. Falls back to reading `founding_member`.
 *
 * Variants:
 *   size = "sm" (default, inline chip) | "md" (card header) | "xs" (comment inline)
 *   showLabel = false → shows an icon-only pill (for tight spaces like DM headers)
 */
export default function MemberBadge({
  member_type,
  founding_member,
  is_official_account,
  size = "sm",
  showLabel = true,
  className = "",
  title,
}) {
  // Session 60.7 · Métier's official brand account renders NO badges. This
  // is a UI-only flag (`is_official_account`) so admin permissions are
  // preserved; we simply do not visually treat it as an individual
  // member. Suppresses Founding Member + Team + Early Adopter across
  // every render surface that goes through MemberBadge.
  if (is_official_account) return null;

  // Session 59.4 · P0 · When `member_type` is missing we render **nothing**
  // rather than silently defaulting to "Early Adopter". The prior fallback
  // caused cam@workmetier.com (a founding member) to appear as an Early
  // Adopter anywhere a caller passed a user object that predated the
  // `member_type` field (e.g. denormalized comment authors, legacy
  // community feed snapshots). No badge is safer than a wrong badge.
  const type =
    member_type ||
    (founding_member === true ? "founding_member" : null);

  if (!type) return null;

  // Session 60.8 · Early Adopter badges retired across the platform per Cam.
  // Founding Member + Métier Team continue to render normally.
  if (type === "early_adopter") return null;

  const configs = {
    founding_member: {
      label: "Founding Member",
      bg: "linear-gradient(135deg,#D6B559 0%,#B8913A 45%,#8B6A22 100%)",
      color: "#FFF8E4",
      border: "rgba(139,106,34,0.55)",
      shadow: "0 1px 0 rgba(255,255,255,0.35) inset, 0 2px 6px rgba(184,145,58,0.28)",
      dot: "#FFF8E4",
      testid: "badge-founding-member",
    },
    early_adopter: {
      label: "Early Adopter",
      bg: "linear-gradient(135deg,#E1E3E8 0%,#A9AFBB 55%,#7A8092 100%)",
      color: "#1B2340",
      border: "rgba(90,96,110,0.55)",
      shadow: "0 1px 0 rgba(255,255,255,0.55) inset, 0 2px 4px rgba(90,96,110,0.22)",
      dot: "#1B2340",
      testid: "badge-early-adopter",
    },
    admin: {
      label: "Métier Team",
      bg: "linear-gradient(135deg,#2A3560 0%,#1B2340 100%)",
      color: "#F5E9CC",
      border: "rgba(184,145,58,0.55)",
      shadow: "0 1px 0 rgba(255,255,255,0.15) inset, 0 2px 5px rgba(27,35,64,0.35)",
      dot: "#B8913A",
      testid: "badge-admin",
    },
  };

  const cfg = configs[type];
  if (!cfg) return null;

  const dims = {
    xs: { padY: 2, padX: 7, fs: 9, ls: "0.12em", gap: 4, dot: 4 },
    sm: { padY: 3, padX: 9, fs: 10, ls: "0.14em", gap: 5, dot: 5 },
    md: { padY: 5, padX: 12, fs: 11, ls: "0.16em", gap: 6, dot: 6 },
  }[size] || { padY: 3, padX: 9, fs: 10, ls: "0.14em", gap: 5, dot: 5 };

  return (
    <span
      data-testid={cfg.testid}
      className={`m-member-badge m-member-badge-${type} ${className}`}
      title={title || cfg.label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: dims.gap,
        padding: `${dims.padY}px ${dims.padX}px`,
        background: cfg.bg,
        color: cfg.color,
        border: `1px solid ${cfg.border}`,
        borderRadius: 999,
        fontSize: dims.fs,
        letterSpacing: dims.ls,
        textTransform: "uppercase",
        fontWeight: 700,
        lineHeight: 1,
        boxShadow: cfg.shadow,
        whiteSpace: "nowrap",
        verticalAlign: "middle",
      }}
    >
      <span
        aria-hidden
        style={{
          width: dims.dot,
          height: dims.dot,
          borderRadius: "50%",
          background: cfg.dot,
          boxShadow: type === "founding_member"
            ? "0 0 0 1.5px rgba(255,255,255,0.5)"
            : "0 0 0 1px rgba(255,255,255,0.4)",
          flexShrink: 0,
        }}
      />
      {showLabel ? cfg.label : null}
    </span>
  );
}
